import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { createPortal } from 'react-dom';
import { KitchenOperationUpdateContext } from '../../../../context-api/data-provider/UpdateOperationDataProvider';
import { GlobalStateDataContext } from '../../../../context-api/data-provider/GlobalStateDataProvider';
import { BRAND, ORANGE, avatarColor, buildInitialStatuses, toTitleCase } from '../constants';

const COL_PX = { driver: 230, accountType: 175, status: 125 };
const GRID_COLS = `${COL_PX.driver}px ${COL_PX.accountType}px ${COL_PX.status}px`;

const DriverAvatar = ({ name }) => {
  const initials = name
    ? name
        .split(/[\s_]/)
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase())
        .join('')
    : '?';

  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: avatarColor(name),
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 700,
        flexShrink: 0,
        letterSpacing: '0.5px',
      }}
    >
      {initials}
    </div>
  );
};

const Truncated = ({ text, style = {} }) => (
  <span
    title={text || ''}
    style={{
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...style,
    }}
  >
    {text || '—'}
  </span>
);

const ColLabel = ({ children, align = 'flex-start' }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: align,
      fontSize: '10.5px',
      fontWeight: 700,
      letterSpacing: '0.9px',
      textTransform: 'uppercase',
      color: '#9ca3af',
      overflow: 'hidden',
    }}
  >
    {children}
  </div>
);

const StatusToggleRow = ({ value, onChange }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 18px',
      border: `1.5px solid ${value ? '#FFD9B8' : '#e4e7ec'}`,
      borderRadius: 10,
      background: value ? '#FFFBF2' : '#fff',
      transition: 'background 0.15s, border-color 0.15s',
      gap: 16,
    }}
  >
    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: BRAND, marginBottom: 3 }}>Driver Status</div>
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
        Enable this driver to allow assignment to delivery orders
      </div>
    </div>
    <div style={{ flexShrink: 0 }}>
      <Switch
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          m: 0,
          '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#ffb98b',
            opacity: 1,
          },
        }}
      />
    </div>
  </div>
);

const AddDriverModal = ({ open, onClose, onAdd, submitting = false, addError = '', onClearError }) => {
  const [driverName, setDriverName] = useState('');
  const [isActive, setIsActive] = useState(true);

  const reset = () => {
    setDriverName('');
    setIsActive(true);
    onClearError?.();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleNameChange = (e) => {
    setDriverName(e.target.value);
    if (addError) onClearError?.();
  };

  const handleSubmit = async () => {
    const trimmed = driverName.trim();
    if (!trimmed) return;
    const ok = await onAdd({ driverName: trimmed, isActive });
    if (ok) {
      reset();
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
    if (e.key === 'Escape') handleClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        backgroundColor: 'rgba(24, 20, 54, 0.4)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 14,
          boxShadow: '0 12px 48px rgba(24, 20, 54, 0.22)',
          width: 440,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '22px 24px 18px',
            borderBottom: '1.5px solid #e8ebef',
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: BRAND, letterSpacing: '-0.1px' }}>
              Add New Driver
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>
              Enter the driver details to add them to your kitchen roster.
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: '1.5px solid #e4e7ec',
              background: '#fff',
              cursor: 'pointer',
              color: '#6b7280',
              flexShrink: 0,
              marginLeft: 12,
              transition: 'background 0.12s, border-color 0.12s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e4e7ec';
            }}
          >
            <CloseOutlinedIcon sx={{ fontSize: 15 }} />
          </button>
        </div>

        <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: '#374151',
                marginBottom: 7,
                letterSpacing: '0.2px',
              }}
            >
              Driver Name
            </label>
            <input
              type='text'
              placeholder='e.g. syedikram2244'
              value={driverName}
              onChange={handleNameChange}
              onKeyDown={handleKeyDown}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 14px',
                border: `1.5px solid ${addError ? '#e53935' : '#e4e7ec'}`,
                borderRadius: 8,
                fontSize: 14,
                color: BRAND,
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                backgroundColor: addError ? '#fff5f5' : '#fafafa',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = addError ? '#e53935' : ORANGE;
                e.target.style.background = '#fff';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = addError ? '#e53935' : '#e4e7ec';
                e.target.style.background = addError ? '#fff5f5' : '#fafafa';
              }}
            />
            {addError && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 7,
                  padding: '8px 12px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 7,
                  fontSize: 12,
                  color: '#b91c1c',
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                <span style={{ fontSize: 15, flexShrink: 0 }}>⚠</span>
                {addError}
              </div>
            )}
          </div>

          <StatusToggleRow value={isActive} onChange={setIsActive} />

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              onClick={handleClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '8px 18px',
                background: '#fff',
                color: '#374151',
                border: '1.5px solid #d1d5db',
                borderRadius: 7,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!driverName.trim() || submitting}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 20px',
                background: driverName.trim() && !submitting ? BRAND : '#c4c9d4',
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                fontSize: 13,
                fontWeight: 600,
                cursor: driverName.trim() && !submitting ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                borderBottom:
                  driverName.trim() && !submitting ? '3px solid rgba(0,0,0,0.25)' : '3px solid transparent',
                transition: 'opacity 0.15s, background 0.15s',
              }}
              onMouseEnter={(e) => {
                if (driverName.trim() && !submitting) e.currentTarget.style.opacity = '0.88';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={14} thickness={6} sx={{ color: '#fff' }} />
                  Adding...
                </>
              ) : (
                <>
                  <PersonAddAlt1OutlinedIcon sx={{ fontSize: 15 }} />
                  Add Driver
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const sortDedicatedDrivers = (list) =>
  [...(list || [])].sort((a, b) => {
    const aKd = String(a?.account_type || '').toLowerCase() === 'kitchendedicateddriver';
    const bKd = String(b?.account_type || '').toLowerCase() === 'kitchendedicateddriver';
    if (aKd === bKd) return 0;
    return aKd ? -1 : 1;
  });

const addDriverErrorMessage = (res) => {
  if (!res) return 'Could not add driver';
  if (res.hasError && res.message) return String(res.message);
  const first = Array.isArray(res.errors) ? res.errors[0] : null;
  if (typeof first === 'string') return first;
  if (first?.errorMessage) return first.errorMessage;
  if (first?.CustomMessage) return first.CustomMessage;
  return (
    res.customMessage ||
    res.message ||
    res.CustomMessage ||
    'Could not add driver'
  );
};

export const DriverListSection = ({ drivers, kitchenName, isRefreshingKitchen = false }) => {
  const { onKitchenOperationUpdateAPI, onAddDedicatedDriverAPI } = useContext(KitchenOperationUpdateContext);
  const { setOpenSnackBar } = useContext(GlobalStateDataContext);
  const [localDrivers, setLocalDrivers] = useState(drivers || []);
  const [statuses, setStatuses] = useState(() => buildInitialStatuses(drivers));
  const [modalOpen, setModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [addError, setAddError] = useState('');
  const isBusy = isSaving || isRefreshingKitchen;

  useEffect(() => {
    setLocalDrivers(drivers || []);
    setStatuses(buildInitialStatuses(drivers || []));
  }, [drivers]);

  const isDirty = useMemo(
    () => (localDrivers || []).some((driver, index) => statuses[driver._id || index] !== Boolean(driver.status)),
    [statuses, localDrivers]
  );

  const handleToggle = useCallback((key) => {
    setStatuses((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleCancel = () => setStatuses(buildInitialStatuses(localDrivers));

  const handleApply = async () => {
    const changedDrivers = (localDrivers || [])
      .map((driver, index) => {
        const key = driver._id || index;
        const nextStatus = Boolean(statuses[key]);
        if (nextStatus === Boolean(driver.status)) return null;
        return {
          driverId: driver.driverId,
          status: nextStatus,
        };
      })
      .filter(Boolean);

    if (!changedDrivers.length) return;

    const payload = {
      kitchen_name: kitchenName,
      dedicated_drivers: changedDrivers,
    };

    setIsSaving(true);
    setIsApplying(true);
    try {
      const response = await onKitchenOperationUpdateAPI(payload);
      if (response?.success || response?.data?.success) {
        const updatedDrivers = (localDrivers || []).map((driver, index) => {
          const key = driver._id || index;
          return { ...driver, status: Boolean(statuses[key]) };
        });
        setLocalDrivers(updatedDrivers);
        setStatuses(buildInitialStatuses(updatedDrivers));
      }
    } finally {
      setIsApplying(false);
      setIsSaving(false);
    }
  };

  const handleAddDriver = async ({ driverName, isActive }) => {
    const trimmed = String(driverName || '').trim();
    if (!trimmed) return false;
    if (!kitchenName) {
      setOpenSnackBar({
        open: true,
        vertical: 'top',
        horizontal: 'right',
        snakBarMessage: 'Kitchen name is missing. Try reloading the page.',
        warning: true,
      });
      return false;
    }

    const payload = {
      kitchen_name: kitchenName,
      driverId: trimmed,
      manager_user_id: kitchenName,
      account_type: 'kitchenDedicatedDriver',
      status: Boolean(isActive),
    };

    setIsSaving(true);
    try {
      const response = await onAddDedicatedDriverAPI(payload);
      if (response?.success === true) {
        const op =
          response.updatedKitchenOperation ||
          response.updated_kitchen_operation;
        const nextList = op?.dedicated_drivers;
        if (Array.isArray(nextList)) {
          const sorted = sortDedicatedDrivers(nextList);
          setLocalDrivers(sorted);
          setStatuses(buildInitialStatuses(sorted));
        } else {
          const fallback = [
            ...(localDrivers || []),
            {
              driverId: trimmed,
              status: Boolean(isActive),
              account_type: 'kitchenDedicatedDriver',
              manager_user_id: kitchenName,
            },
          ];
          const sorted = sortDedicatedDrivers(fallback);
          setLocalDrivers(sorted);
          setStatuses(buildInitialStatuses(sorted));
        }
        return true;
      }

      setAddError(addDriverErrorMessage(response));
      return false;
    } catch {
      setAddError('Something went wrong. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const isEmpty = !localDrivers || localDrivers.length === 0;

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', padding: '24px 24px 28px', position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1.5px solid #e8ebef',
          flexWrap: 'wrap',
          gap: 10,
          paddingBottom: '18px',
          marginBottom: '22px',
        }}
      >
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: BRAND }}>Driver Roster</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: 2 }}>
            Manage driver or add new drivers to your kitchen
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!isEmpty && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 14px',
                backgroundColor: '#fffbf2',
                border: '1px solid #fbedcd',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 700,
                color: ORANGE,
              }}
            >
              <LocalShippingOutlinedIcon sx={{ fontSize: 14, color: ORANGE }} />
              {localDrivers.length} {localDrivers.length === 1 ? 'Driver' : 'Drivers'}
            </div>
          )}

          <button
            onClick={() => setModalOpen(true)}
            disabled={isBusy}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 16px',
              backgroundColor: BRAND,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: isBusy ? 'not-allowed' : 'pointer',
              opacity: isBusy ? 0.6 : 1,
              letterSpacing: '0.2px',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.85';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <PersonAddAlt1OutlinedIcon sx={{ fontSize: 16 }} />
            Add Driver
          </button>
        </div>
      </div>

      {isEmpty ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            gap: 12,
          }}
        >
          <LocalShippingOutlinedIcon sx={{ fontSize: 52, color: '#d1d5db' }} />
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>No drivers assigned</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', maxWidth: 300 }}>
            Dedicated drivers linked to your kitchen will appear here.
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'inline-block',
              border: '1.5px solid #e2e5ea',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: GRID_COLS,
                width: 'max-content',
                padding: '10px 24px',
                gap: '0 12px',
                backgroundColor: '#f4f5f7',
                borderBottom: '1.5px solid #e2e5ea',
              }}
            >
              <ColLabel>Driver</ColLabel>
              <ColLabel>Account Type</ColLabel>
              <ColLabel align='center'>Status</ColLabel>
            </div>

            {localDrivers.map((driver, index) => {
              const key = driver._id || index;
              const active = Boolean(statuses[key]);
              const isKitchenDedicatedDriver =
                String(driver.account_type || '').toLowerCase() === 'kitchendedicateddriver';
              const changed = active !== Boolean(driver.status);
              return (
                <div
                  key={key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLS,
                    width: 'max-content',
                    alignItems: 'center',
                    padding: '13px 24px',
                    gap: '0 12px',
                    borderBottom: '1px solid #e8ebef',
                    backgroundColor: changed ? '#fffbf2' : 'transparent',
                    transition: 'background 0.12s',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    if (!changed) e.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = changed ? '#fffbf2' : 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
                    <DriverAvatar name={driver.driverId} />
                    <div style={{ minWidth: 0 }}>
                      <Truncated
                        text={driver.driverId}
                        style={{ fontWeight: 600, color: BRAND, fontSize: '13px' }}
                      />
                      <div style={{ fontSize: '11px', color: '#b0b7c3', marginTop: 1, letterSpacing: '0.2px' }}>
                        Driver ID
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                    <BadgeOutlinedIcon sx={{ fontSize: 13, color: '#c4c9d4', flexShrink: 0 }} />
                    <Truncated
                      text={toTitleCase(driver.account_type)}
                      style={{
                        fontSize: '11.5px',
                        fontWeight: 600,
                        color: '#5e35b1',
                        backgroundColor: '#f3f0ff',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        border: '1px solid #d1c4e9',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    {isKitchenDedicatedDriver && (
                      <Tooltip title={active ? 'Click to deactivate' : 'Click to activate'} placement='top' arrow>
                        <Switch
                          checked={active}
                          onChange={() => handleToggle(key)}
                          size='small'
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#66bb6a',
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                    <Chip
                      label={active ? 'Active' : 'Inactive'}
                      size='small'
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        height: 22,
                        minWidth: 62,
                        backgroundColor: active ? '#e8f5e9' : '#fce4ec',
                        color: active ? '#2e7d32' : '#c62828',
                        border: `1px solid ${active ? '#c8e6c9' : '#ef9a9a'}`,
                        borderRadius: '999px',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              overflow: 'hidden',
              maxHeight: isDirty ? '80px' : '0px',
              opacity: isDirty ? 1 : 0,
              transition: 'max-height 0.25s ease, opacity 0.2s ease',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 24px',
                borderTop: '1.5px solid #e8ebef',
                backgroundColor: '#fffbf2',
              }}
            >
              <span style={{ fontSize: '13px', color: '#6b7280' }}>You have unsaved status changes.</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleCancel}
                  disabled={isBusy}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '7px 16px',
                    backgroundColor: '#fff',
                    color: '#374151',
                    border: '1.5px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: isBusy ? 'not-allowed' : 'pointer',
                    opacity: isBusy ? 0.7 : 1,
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={isBusy}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '7px 18px',
                    backgroundColor: '#2e7d32',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: isBusy ? 'not-allowed' : 'pointer',
                    opacity: isBusy ? 0.7 : 1,
                    borderBottom: '3px solid #1b5e20',
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.88';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  {isApplying ? (
                    <>
                      <CircularProgress size={14} thickness={6} sx={{ color: '#fff' }} />
                      Applying...
                    </>
                  ) : (
                    <>
                      <CheckOutlinedIcon sx={{ fontSize: 14 }} />
                      Apply Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <AddDriverModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setAddError(''); }}
        onAdd={handleAddDriver}
        submitting={isBusy}
        addError={addError}
        onClearError={() => setAddError('')}
      />

      {isRefreshingKitchen && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.62)',
            backdropFilter: 'blur(1px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 4,
            borderRadius: 10,
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 8,
              background: '#fff',
              border: '1.5px solid #e8ebef',
              color: '#4b5563',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <CircularProgress size={16} thickness={6} sx={{ color: ORANGE }} />
            Refreshing driver roster...
          </div>
        </div>
      )}
    </div>
  );
};
